/**
 * PageEmptyState — consistent empty state for the primary pages
 * (#498). The CTA reuses CHECKLIST_ACTIONS from the setup checklist so
 * empty-state routes and Dashboard deep-links can never drift apart; a
 * custom action (e.g. "New Spec") can be passed instead when the page
 * has a more direct next step.
 */

import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CHECKLIST_ACTIONS, type ChecklistActionKey } from './SetupChecklist';

export function PageEmptyState({
  icon: Icon,
  title,
  description,
  action,
  customAction,
  disabledNote,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  /** Checklist action key — CTA label and route come from the checklist. */
  action?: ChecklistActionKey;
  /** Overrides `action` when the page has a more direct next step. */
  customAction?: { label: string; to: string };
  /** The page's capability is deliberately disabled (guided-repo-setup
   *  Phase D): the setup CTA gives way to this enable-in-Settings note —
   *  a decided opt-out must never be nudged like an omission. */
  disabledNote?: { message: string; enableHref?: string };
  children?: ReactNode;
}) {
  const cta = disabledNote ? null : customAction ?? (action ? CHECKLIST_ACTIONS[action] : null);

  return (
    <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
      <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/10 to-blue-500/10 dark:from-violet-500/20 dark:to-blue-500/20">
        <Icon className="h-7 w-7 text-violet-600 dark:text-violet-400" />
      </div>
      <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
      <p className="mb-6 max-w-md text-sm text-slate-500 dark:text-slate-400">{description}</p>
      {disabledNote && (
        <p className="max-w-md text-sm text-slate-500 dark:text-slate-400">
          {disabledNote.message}
          {disabledNote.enableHref && (
            <>
              {' '}
              <a href={disabledNote.enableHref} className="text-blue-600 hover:underline dark:text-blue-400">
                Enable in Settings
              </a>
            </>
          )}
        </p>
      )}
      {cta && (/^https?:/.test(cta.to) ? (
        // Absolute target — a cross-surface hop (e.g. capture → console
        // Settings), so a plain anchor instead of a router Link.
        <a
          href={cta.to}
          className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700"
        >
          {cta.label} <ArrowRight className="h-4 w-4" />
        </a>
      ) : (
        <Link
          to={cta.to}
          className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700"
        >
          {cta.label} <ArrowRight className="h-4 w-4" />
        </Link>
      ))}
      {children}
    </div>
  );
}
